import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tabs,
  Tab,
  TextField,
  Box,
  Pagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faArchive,
  faUsers,
  faEllipsisV,
  faEye,
  faEyeSlash,
  faLock,
  faLockOpen,
  faSearch,
  faUpload,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/AuthContext";
import CreateCourseDialog from "../../components/Admin/CreateCourseDialog";
import UploadInvitationFileDialog from "../../components/Admin/UploadInvitationFileDialog";
import CourseService from "../../services/courseService";
import Course from "../../models/Course";
import { useQueryClient } from "@tanstack/react-query";
import InvitedParticipantsDialog from "../../components/Admin/InvitedParticipantsDialog";
import useNotification from "../../hooks/useNotification";
import CourseRoster from "../../components/Admin/CourseRoster";
import { formatDate } from "../../shared/helperFunctions";

const CourseManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [isRosterOpen, setIsRosterOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCourse, setMenuCourse] = useState<Course | null>(null);
  const [activeTab, setActiveTab] = useState<"legacy" | "tactical" | "online">(
    "legacy"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<keyof Course>("startDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [isUploadInvitationDialogOpen, setIsUploadInvitationDialogOpen] =
    useState(false);
  const [isParticipantViewOpen, setIsParticipantViewOpen] = useState(false);

  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const handleOpenDialog = (course: Course | null = null) => {
    setSelectedCourse(course);
    setIsDialogOpen(true);
  };

  const handleParticipantViewChange = (isOpen: boolean) => {
    setIsParticipantViewOpen(isOpen);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleOpenInvitationModal = (course: Course) => {
    setSelectedCourseId(course.id.toString());
    handleCloseMenu();
    setIsInvitationModalOpen(true);
  };

  const handleCloseInvitationModal = () => {
    setIsInvitationModalOpen(false);
    setSelectedCourseId(null);
  };

  const handleUploadInvitations = (course: Course) => {
    handleCloseMenu();
    setSelectedCourseId(course.id.toString());
    setIsUploadInvitationDialogOpen(true);
  };

  const handleCloseUploadInvitationModal = () => {
    setIsUploadInvitationDialogOpen(false);
    setSelectedCourseId(null);
  };

  const handleSubmit = async (
    formData: FormData
  ): Promise<{ success: boolean; message: string }> => {
    try {
      if (selectedCourse) {
        await CourseService.updateCourse(selectedCourse.id, formData);
      } else {
        await CourseService.createCourse(formData);
      }
      queryClient.refetchQueries({ queryKey: ["courses"] });
      await loadCourses();
      handleCloseDialog();

      return {
        success: true,
        message: `Course ${
          selectedCourse ? "updated" : "created"
        } successfully!`,
      };
    } catch (error) {
      console.error(
        `Failed to ${selectedCourse ? "update" : "create"} course:`,
        error
      );
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : `Failed to ${
                selectedCourse ? "update" : "create"
              } course. Please try again.`,
      };
    }
  };

  const loadCourses = async () => {
    setIsLoading(true);
    try {
      const fetchedCourses = await CourseService.fetchCourses({
        type: activeTab,
        page,
        limit,
        sortBy,
        sortOrder,
        search: appliedSearchTerm,
      });
      if (fetchedCourses && Array.isArray(fetchedCourses.courses)) {
        setCourses(fetchedCourses.courses);
        setTotal(fetchedCourses.total);
      } else {
        console.error(
          "Courses is not an array or data is missing:",
          fetchedCourses
        );
      }
    } catch (err) {
      setError("Failed to load courses");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, [activeTab, page, limit, sortBy, sortOrder, appliedSearchTerm]);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    course: Course
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuCourse(course);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuCourse(null);
  };

  const handleEditCourse = (course: Course) => {
    handleCloseMenu();
    handleOpenDialog(course);
  };

  const handleArchiveCourse = async (course: Course) => {
    try {
      handleCloseMenu();
      await CourseService.archiveCourse(course.id);
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      await loadCourses();
    } catch (error) {
      console.error("Failed to delete course:", error);
      setError("Failed to delete course. Please try again.");
    }
  };

  const handleViewRoster = (course: Course) => {
    handleCloseMenu();
    setSelectedCourse(course);
    setIsRosterOpen(true);
  };

  // const formatDate = (date: Date) => {
  //   return dayjs(date).format("MMM D, YYYY h:mm A");
  // };

  const handleSort = (property: keyof Course) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortBy(property);
    setSortOrder(isAsc ? "desc" : "asc");
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setPage(1); // Reset to first page when searching
  };

  const handleDownloadPdf = useCallback((pdfUrl: string | null) => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  }, []);

  const renderTableHeaders = () => {
    const headers: { key: keyof Course; label: string }[] = [
      { key: "name", label: "Course Name" },
      { key: "location", label: "Location" },
      { key: "createdBy", label: "Created By" },
    ];

    if (activeTab !== "online") {
      headers.unshift(
        { key: "startDate", label: "Start Date" },
        { key: "endDate", label: "End Date" }
      );
    }

    return (
      <>
        {headers.map((header) => (
          <TableCell key={header.key}>
            <TableSortLabel
              active={sortBy === header.key}
              direction={sortBy === header.key ? sortOrder : "asc"}
              onClick={() => handleSort(header.key)}
            >
              {header.label}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell>Status</TableCell>
        <TableCell>Actions</TableCell>
      </>
    );
  };

  const renderTableRow = (course: Course) => {
    const commonCells = [
      <TableCell key="name">{course.name}</TableCell>,
      <TableCell key="location">{course.location}</TableCell>,
      <TableCell key="createdBy">{`${course.createdBy.firstName} ${course.createdBy.lastName}`}</TableCell>,
      <TableCell key="status">
        <FontAwesomeIcon
          icon={course.visible ? faEye : faEyeSlash}
          color={course.visible ? "blue" : "gray"}
        />{" "}
        <FontAwesomeIcon
          icon={course.openEnroll ? faLockOpen : faLock}
          color={course.openEnroll ? "blue" : "gray"}
        />
      </TableCell>,
      <TableCell key="actions">
        <IconButton onClick={(e) => handleOpenMenu(e, course)}>
          <FontAwesomeIcon icon={faEllipsisV} />
        </IconButton>
      </TableCell>,
    ];

    if (activeTab !== "online") {
      return [
        <TableCell key="startDate">{formatDate(course.startDate)}</TableCell>,
        <TableCell key="endDate">{formatDate(course.endDate)}</TableCell>,
        ...commonCells,
      ];
    }

    return commonCells;
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Course Management
      </Typography>
      <Typography variant="subtitle1" gutterBottom>
        Welcome, {user?.firstName}. Manage your courses here.
      </Typography>

      <Dialog
        open={isRosterOpen}
        onClose={() => setIsRosterOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          width: "650px",
          maxWidth: "650px",
        }}
      >
        {/* <DialogTitle>
          Course Roster for {selectedCourse?.name} (Max:{" "}
          {selectedCourse?.maxStudents})
        </DialogTitle> */}
        <DialogContent
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 64px)",
            overflow: "hidden",
          }}
        >
          {selectedCourse && (
            <CourseRoster
              course={selectedCourse}
              onParticipantViewChange={handleParticipantViewChange}
            />
          )}
        </DialogContent>
        <DialogActions>
          {!isParticipantViewOpen && (
            <Button onClick={() => setIsRosterOpen(false)}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
      >
        <Tab label="Legacy Course" value="legacy" />
        <Tab label="Tactical Course" value="tactical" />
        <Tab label="Online Courses" value="online" />
      </Tabs>

      <Box
        sx={{
          my: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{ ml: 1 }}
          >
            <FontAwesomeIcon icon={faSearch} />
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>{renderTableHeaders()}</TableRow>
          </TableHead>
          <TableBody>
            {courses.map((course) => (
              <TableRow key={course.id}>{renderTableRow(course)}</TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Pagination
          count={Math.ceil(total / limit)}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
        />
        <Typography>
          Showing {Math.min((page - 1) * limit + 1, total)} -{" "}
          {Math.min(page * limit, total)} of {total} courses
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        onClick={() => handleOpenDialog()}
      >
        Add New Course
      </Button>

      <CreateCourseDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        course={selectedCourse}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => menuCourse && handleEditCourse(menuCourse)}>
          <FontAwesomeIcon icon={faEdit} style={{ marginRight: "8px" }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => menuCourse && handleArchiveCourse(menuCourse)}>
          <FontAwesomeIcon icon={faArchive} style={{ marginRight: "8px" }} />{" "}
          Archive
        </MenuItem>
        <MenuItem onClick={() => menuCourse && handleViewRoster(menuCourse)}>
          <FontAwesomeIcon icon={faUsers} style={{ marginRight: "8px" }} /> View
          Roster
        </MenuItem>
        <MenuItem
          onClick={() => menuCourse && handleOpenInvitationModal(menuCourse)}
        >
          <FontAwesomeIcon icon={faUsers} style={{ marginRight: "8px" }} /> View
          Invitations
        </MenuItem>
        <MenuItem
          onClick={() => menuCourse && handleUploadInvitations(menuCourse)}
        >
          <FontAwesomeIcon icon={faUpload} style={{ marginRight: "8px" }} />{" "}
          Upload Invitations
        </MenuItem>
        {menuCourse?.pdfFileUrl && (
          <MenuItem onClick={() => handleDownloadPdf(menuCourse.pdfFileUrl)}>
            <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: "8px" }} />{" "}
            Download PDF
          </MenuItem>
        )}
      </Menu>
      <InvitedParticipantsDialog
        open={isInvitationModalOpen}
        onClose={handleCloseInvitationModal}
        courseId={selectedCourseId || ""}
      />
      <UploadInvitationFileDialog
        open={isUploadInvitationDialogOpen}
        onClose={handleCloseUploadInvitationModal}
        courseId={selectedCourseId || ""}
      />
    </Container>
  );
};

export default CourseManagementPage;
